import { assert } from "./ErrorHandling";
import { getOperationSystem } from "./Misc";
import { OperatingSystems } from "../../globals";
import Input from "../Input";
import { KeyCodes, ModifierKeyCodes } from "../../interfaces/Input";

/**
 * converts a given keybinding object into a human-readable string for UI display
 * @static
 * @param {{modifiers: ModifierKeyCodes, keycode: String}} keybinding an object that contains the 'modifiers' and 'keycode' properties, defining a key combination that when pressed should execute an event
 * @returns a neatly formatted string that shows which key combination makes up the keybinding, e.g. "Ctrl + A"
 * @memberof Utils
*/
export function getKeybindingString(keybinding): string
{
	function getKeyName(keycode: number): string
	{
		if (keycode >= 48 && keycode <= 90) return String.fromCharCode(keycode);
		if (keycode < 0) return "";

		switch (keycode)
		{
			case KeyCodes.BACKSPACE: return 'Backspace';
			case KeyCodes.TAB: return 'Tab';
			case KeyCodes.ENTER: return 'Enter';
			case KeyCodes.PAUSE: return 'Pause';
			case KeyCodes.CAPS_LOCK: return 'CapsLock';
			case KeyCodes.ESCAPE: return 'Escape';
			case KeyCodes.SPACE: return 'Space';
			case KeyCodes.PAGE_UP: return 'PageUp';
			case KeyCodes.PAGE_DOWN: return 'PageDown';
			case KeyCodes.END: return 'End';
			case KeyCodes.HOME: return 'Home';
			case KeyCodes.LEFT_ARROW: return 'Left Arrow';
			case KeyCodes.UP_ARROW: return 'Up Arrow';
			case KeyCodes.RIGHT_ARROW: return 'Right Arrow';
			case KeyCodes.DOWN_ARROW: return 'Down Arrow';
			case KeyCodes.INSERT: return 'Insert';
			case KeyCodes.DELETE: return 'Delete';
			case KeyCodes.NUMPAD_0: return 'Numpad 0';
			case KeyCodes.NUMPAD_1: return 'Numpad 1';
			case KeyCodes.NUMPAD_2: return 'Numpad 2';
			case KeyCodes.NUMPAD_3: return 'Numpad 3';
			case KeyCodes.NUMPAD_4: return 'Numpad 4';
			case KeyCodes.NUMPAD_5: return 'Numpad 5';
			case KeyCodes.NUMPAD_6: return 'Numpad 6';
			case KeyCodes.NUMPAD_7: return 'Numpad 7';
			case KeyCodes.NUMPAD_8: return 'Numpad 8';
			case KeyCodes.NUMPAD_9: return 'Numpad 9';
			case KeyCodes.n0: return '0';
			case KeyCodes.n1: return '1';
			case KeyCodes.n2: return '2';
			case KeyCodes.n3: return '3';
			case KeyCodes.n4: return '4';
			case KeyCodes.n5: return '5';
			case KeyCodes.n6: return '6';
			case KeyCodes.n7: return '7';
			case KeyCodes.n8: return '8';
			case KeyCodes.n9: return '9';
			case KeyCodes.MULTIPLY: return '*';
			case KeyCodes.ADD: return '+';
			case KeyCodes.SUBTRACT: return '-';
			case KeyCodes.F1: return 'F1';
			case KeyCodes.F2: return 'F2';
			case KeyCodes.F3: return 'F3';
			case KeyCodes.F4: return 'F4';
			case KeyCodes.F5: return 'F5';
			case KeyCodes.F6: return 'F6';
			case KeyCodes.F7: return 'F7';
			case KeyCodes.F8: return 'F8';
			case KeyCodes.F9: return 'F9';
			case KeyCodes.F10: return 'F10';
			case KeyCodes.F11: return 'F11';
			case KeyCodes.F12: return 'F12';
			case KeyCodes.NUM_LOCK: return 'NumLock';
			case KeyCodes.SEMICOLON: return ';';
			case KeyCodes.EQUALS: return '=';
			case KeyCodes.COMMA: return ',';
			case KeyCodes.DASH: return '-';
			case KeyCodes.MINUS_FIREFOX: return '-';
			case KeyCodes.PERIOD: return '.';
			case KeyCodes.FORWARD_SLASH: return '/';
			case KeyCodes.GRAVE_ACCENT: return '~';

			default:
				return String.fromCharCode(keycode);
		}
	}

	let output = "";
	if (!keybinding)
	{ return output; }

	if (keybinding.modifiers)
	{
		for (const modifier of keybinding.modifiers)
		{
			let modifierName;
			if (Array.isArray(modifier))
			{
				if (JSON.stringify(modifier) == JSON.stringify(ModifierKeyCodes.COMMAND))
				{
					modifierName = "Command";
				}
				else
				{
					for (const optionalModifier of modifier)
					{
						if (!optionalModifier) continue;
						if (optionalModifier === ModifierKeyCodes.CTRL)
						{
							modifierName = "Ctrl";
						}
						else if (optionalModifier === ModifierKeyCodes.SHIFT)
						{
							modifierName = "Shift";
						}
						else if (optionalModifier === ModifierKeyCodes.ALT)
						{
							modifierName = "Alt";
						}
						else if (getOperationSystem() === OperatingSystems.MacOS && Array.isArray(optionalModifier) && JSON.stringify(optionalModifier) === JSON.stringify(ModifierKeyCodes.COMMAND))
						{
							modifierName = "Command";
						}
					}
				}
			}
			else
			{
				if (modifier === ModifierKeyCodes.CTRL) modifierName = "Ctrl";
				else if (modifier === ModifierKeyCodes.SHIFT) modifierName = "Shift";
				else if (modifier === ModifierKeyCodes.ALT)
				{
					modifierName = "Alt";
					if(getOperationSystem() === OperatingSystems.MacOS)
					{
						modifierName = "Option";
					}
				}
			}
			assert(modifierName);
			output = output.concat(modifierName + " + ");
		}
	}
	output = output.concat(getKeyName(keybinding.keycode));
	return output;
}